export const catalogPhoto = (product) => {
  const images = Array.isArray(product.images) ? product.images : [];
  return images[product.main_image_index || 0] || images[0] || null;
};

export function catalogLayout(perPage) {
  if (!Number.isInteger(perPage) || perPage < 1 || perPage > 36) throw new Error('Elige entre 1 y 36 fotos por hoja');
  const columns = Math.ceil(Math.sqrt(perPage));
  return { columns, rows: Math.ceil(perPage / columns) };
}

export async function collectCatalogProducts(fetchPage, filters) {
  const products = [];
  for (let offset = 0; ; offset += 250) {
    const result = await fetchPage({ ...filters, offset, limit: 250 });
    if (result.error) throw result.error;
    const batch = result.data || [];
    products.push(...batch);
    if (batch.length < 250) break;
  }
  return products;
}

export async function imageBytes(source) {
  const image = new Image();
  image.crossOrigin = 'anonymous';
  await new Promise((resolve, reject) => {
    const timeout = setTimeout(() => reject(new Error('Tiempo de carga de imagen agotado')), 30000);
    image.onload = () => { clearTimeout(timeout); resolve(); };
    image.onerror = () => { clearTimeout(timeout); reject(new Error('No se pudo cargar la foto')); };
    image.src = source;
  });
  const scale = Math.min(1, 1800 / Math.max(image.naturalWidth, image.naturalHeight));
  const canvas = document.createElement('canvas');
  canvas.width = Math.max(1, Math.round(image.naturalWidth * scale));
  canvas.height = Math.max(1, Math.round(image.naturalHeight * scale));
  const context = canvas.getContext('2d');
  context.drawImage(image, 0, 0, canvas.width, canvas.height);
  return canvas.toDataURL('image/png');
}

function wrapText(text, font, size, width) {
  const lines = []; let line = '';
  for (const character of text) {
    if (character === '\n' || (line && font.widthOfTextAtSize(line + character, size) > width)) {
      lines.push(line); line = '';
    }
    if (character !== '\n') line += character;
  }
  if (line) lines.push(line);
  return lines;
}

export async function createPhotoCatalog(products, perPage, { loadImage = imageBytes, onProgress = () => {} } = {}) {
  const { PDFDocument, StandardFonts, rgb } = await import('pdf-lib');
  const { columns, rows } = catalogLayout(perPage);
  const pdf = await PDFDocument.create();
  const font = await pdf.embedFont(StandardFonts.Helvetica);
  const bold = await pdf.embedFont(StandardFonts.HelveticaBold);
  const safeText = (value) => Array.from(String(value || '')).map((char) => {
    if (char === '\n') return char;
    try { font.encodeText(char); return char; } catch { return '?'; }
  }).join('');
  const pageWidth = 595.28, pageHeight = 841.89, margin = 28, gap = 12;
  const width = (pageWidth - margin * 2 - gap * (columns - 1)) / columns;
  const height = (pageHeight - margin * 2 - 16 - gap * (rows - 1)) / rows;
  for (let index = 0; index < products.length; index++) {
    const position = index % perPage;
    if (!position) pdf.addPage([pageWidth, pageHeight]);
    const page = pdf.getPages().at(-1);
    const product = products[index];
    const x = margin + (position % columns) * (width + gap);
    const top = pageHeight - margin - Math.floor(position / columns) * (height + gap);
    const textHeight = Math.min(64, height * 0.3);
    let size = Math.min(11, width / 12);
    let codeLines, descriptionLines;
    do {
      codeLines = wrapText(safeText(product.code), bold, size, width);
      descriptionLines = wrapText(safeText(product.description), font, size, width);
      if ((codeLines.length + descriptionLines.length) * size * 1.2 <= textHeight) break;
      size *= 0.9;
    } while (size > 1);
    let y = top - size;
    for (const [lines, face] of [[codeLines, bold], [descriptionLines, font]]) {
      for (const line of lines) { page.drawText(line, { x, y, size, font: face }); y -= size * 1.2; }
    }
    let photo;
    try { photo = await pdf.embedPng(await loadImage(catalogPhoto(product))); }
    catch (error) { throw new Error(`No se pudo cargar la foto de ${product.code}: ${error.message}`); }
    const imageHeight = height - textHeight - 8;
    const fitted = photo.scaleToFit(width, imageHeight);
    page.drawImage(photo, { x: x + (width - fitted.width) / 2, y: top - height + (imageHeight - fitted.height) / 2, ...fitted });
    onProgress(index + 1, products.length);
  }
  pdf.getPages().forEach((page, index) => page.drawText(`${index + 1} / ${pdf.getPageCount()}`, { x: margin, y: 16, size: 8, font, color: rgb(0.4, 0.4, 0.4) }));
  return pdf.save();
}
